import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Link,
  Checkbox
} from '@mui/material';
import { Refresh, Delete, Sync } from '@mui/icons-material';
import { format } from 'date-fns';
import { authGet, authDelete } from '../../services/apiService';
import WebhookQueueStatus from './WebhookQueueStatus';

const EVENT_LABELS = {
  'workitem.created': 'Created',
  'workitem.updated': 'Updated',
  'workitem.deleted': 'Deleted'
};

/**
 * WebhookLogViewer – summary by day with created/updated/deleted counts as links;
 * per-row delete, select days and delete selected.
 */
const WebhookLogViewer = () => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entriesDialog, setEntriesDialog] = useState({ open: false, date: null, eventType: null, label: '' });
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [jsonDialog, setJsonDialog] = useState({ open: false, payload: null });
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reconcileDialog, setReconcileDialog] = useState({ open: false, date: null, loading: false, result: null, error: null });

  const loadSummary = async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authGet('/api/admin/webhook-logs/summary', null, 'json', options);
      if (response.success && response.data) {
        setSummary(Array.isArray(response.data) ? response.data : []);
      } else {
        setError(response.message || 'Failed to load webhook summary');
        setSummary([]);
      }
    } catch (err) {
      console.error('Error loading webhook summary:', err);
      if (err.message?.includes('403')) {
        setError('You do not have permission to view webhook logs.');
      } else if (err.message?.includes('401')) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(`Error loading webhook summary: ${err.message}`);
      }
      setSummary([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleOpenEntries = async (date, eventType) => {
    setEntriesDialog({ open: true, date, eventType, label: EVENT_LABELS[eventType] || eventType });
    setEntries([]);
    setEntriesLoading(true);
    try {
      const response = await authGet(
        `/api/admin/webhook-logs/entries?date=${encodeURIComponent(date)}&eventType=${encodeURIComponent(eventType)}`
      );
      if (response.success && response.data) {
        setEntries(Array.isArray(response.data) ? response.data : []);
      } else {
        setEntries([]);
      }
    } catch (err) {
      console.error('Error loading webhook entries:', err);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleCloseEntries = () => {
    setEntriesDialog({ open: false, date: null, eventType: null, label: '' });
    setEntries([]);
  };

  const handleOpenJson = (payload) => {
    setJsonDialog({ open: true, payload });
  };

  const handleCloseJson = () => {
    setJsonDialog({ open: false, payload: null });
  };

  const handleDeleteDates = async (datesToDelete) => {
    const datesArray = Array.isArray(datesToDelete) ? datesToDelete : [datesToDelete];
    const normalized = datesArray.map((d) => (d != null ? String(d).trim() : '')).filter(Boolean);
    if (normalized.length === 0) return;
    try {
      setDeleteLoading(true);
      setError(null);
      const query = normalized.map((d) => encodeURIComponent(d)).join(',');
      const response = await authDelete(`/api/admin/webhook-logs/by-dates?dates=${query}`);
      setSelectedDates((prev) => {
        const next = new Set(prev);
        normalized.forEach((d) => next.delete(d));
        return next;
      });
      await loadSummary({ useCache: false });
      if (!response.success) {
        setError(response.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting webhook logs:', err);
      setError(err.message || 'Delete failed');
      await loadSummary({ useCache: false });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleSelect = (date) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelectedDates(new Set(summary.map((row) => row.date)));
    else setSelectedDates(new Set());
  };

  const handleReconcile = async (date) => {
    setReconcileDialog({ open: true, date, loading: true, result: null, error: null });
    try {
      const response = await authGet(`/api/admin/webhook-logs/reconcile?date=${encodeURIComponent(date)}`, null, 'json', { useCache: false });
      if (response.success && response.data) {
        setReconcileDialog((prev) => ({ ...prev, loading: false, result: response.data }));
      } else {
        setReconcileDialog((prev) => ({ ...prev, loading: false, error: response.message || 'Reconcile failed' }));
      }
    } catch (err) {
      console.error('Error reconciling:', err);
      setReconcileDialog((prev) => ({ ...prev, loading: false, error: err.message || 'Reconcile failed' }));
    }
  };

  const handleCloseReconcile = () => {
    setReconcileDialog({ open: false, date: null, loading: false, result: null, error: null });
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString + 'T12:00:00'), 'yyyy-MM-dd');
    } catch {
      return dateString || '—';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Webhook Logs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Events received at <code>POST /api/ado-webhook</code>, grouped by day. Click a number to see work item IDs and view JSON. Select days and delete, or delete a single day.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadSummary} disabled={loading} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Delete />}
            onClick={() => handleDeleteDates([...selectedDates])}
            disabled={loading || deleteLoading || selectedDates.size === 0}
          >
            {deleteLoading ? 'Deleting…' : `Delete selected (${selectedDates.size})`}
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedDates.size > 0 && selectedDates.size < summary.length}
                    checked={summary.length > 0 && selectedDates.size === summary.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={loading || summary.length === 0}
                  />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Created</TableCell>
                <TableCell align="right">Updated</TableCell>
                <TableCell align="right">Deleted</TableCell>
                <TableCell padding="none" align="right" width={120} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : summary.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No webhook events yet. Configure ADO Service Hooks to send work item events to this app.
                  </TableCell>
                </TableRow>
              ) : (
                summary.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDates.has(row.date)}
                        onChange={() => handleToggleSelect(row.date)}
                        disabled={deleteLoading}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.date)}</TableCell>
                    <TableCell align="right">
                      {row.created > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleOpenEntries(row.date, 'workitem.created')}
                          sx={{ cursor: 'pointer' }}
                        >
                          {row.created}
                        </Link>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.updated > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleOpenEntries(row.date, 'workitem.updated')}
                          sx={{ cursor: 'pointer' }}
                        >
                          {row.updated}
                        </Link>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row.deleted > 0 ? (
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleOpenEntries(row.date, 'workitem.deleted')}
                          sx={{ cursor: 'pointer' }}
                        >
                          {row.deleted}
                        </Link>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell padding="none" align="right">
                      <Tooltip title="Reconcile with ADO">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleReconcile(row.date)}
                          disabled={reconcileDialog.loading}
                        >
                          <Sync />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete this day">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleDeleteDates([row.date])}
                          disabled={deleteLoading}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog: hyperlinks to work items (5 per row), click opens JSON */}
      <Dialog open={entriesDialog.open} onClose={handleCloseEntries} maxWidth="md" fullWidth PaperProps={{ sx: { minWidth: 560 } }}>
        <DialogTitle>
          {entriesDialog.date} – {entriesDialog.label}
        </DialogTitle>
        <DialogContent>
          {entriesLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : entries.length === 0 ? (
            <Typography color="text.secondary">No entries</Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 1.5,
                py: 1
              }}
            >
              {entries.map((entry) => (
                <Link
                  key={entry._id}
                  component="button"
                  variant="body2"
                  onClick={() => handleOpenJson(entry.payload)}
                  sx={{ cursor: 'pointer', textAlign: 'left', justifyContent: 'flex-start' }}
                >
                  {entry.workItemId ?? '—'}
                </Link>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntries}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: full JSON payload */}
      <Dialog open={jsonDialog.open} onClose={handleCloseJson} maxWidth="md" fullWidth>
        <DialogTitle>Webhook payload</DialogTitle>
        <DialogContent>
          {jsonDialog.payload != null && (
            <Paper variant="outlined" sx={{ p: 2, overflow: 'auto', maxHeight: '70vh' }}>
              <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(jsonDialog.payload, null, 2)}
              </pre>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseJson}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Reconcile result */}
      <Dialog open={reconcileDialog.open} onClose={handleCloseReconcile} maxWidth="sm" fullWidth>
        <DialogTitle>Reconcile – {reconcileDialog.date}</DialogTitle>
        <DialogContent>
          {reconcileDialog.loading && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 1 }}>Querying ADO for work items updated on this date…</Typography>
            </Box>
          )}
          {reconcileDialog.error && (
            <Alert severity="error" sx={{ mt: 1 }}>{reconcileDialog.error}</Alert>
          )}
          {!reconcileDialog.loading && reconcileDialog.result && (
            <Box sx={{ pt: 1 }}>
              {reconcileDialog.result.query && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>ADO query used</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
                    <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                      {reconcileDialog.result.query}
                    </Typography>
                  </Paper>
                </>
              )}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Matched: {reconcileDialog.result.matchedCount} work item(s)
              </Typography>
              {reconcileDialog.result.onlyInWebhooksCount > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                    Only in webhooks (not in ADO for this date): {reconcileDialog.result.onlyInWebhooksCount}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 2 }}>
                    {reconcileDialog.result.onlyInWebhooks.map((id) => (
                      <Typography key={id} variant="body2" component="span">{id}</Typography>
                    ))}
                  </Box>
                </>
              )}
              {reconcileDialog.result.onlyInAdoCount > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                    Only in ADO (no webhook received): {reconcileDialog.result.onlyInAdoCount}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
                    {reconcileDialog.result.onlyInAdo.map((id) => (
                      <Typography key={id} variant="body2" component="span">{id}</Typography>
                    ))}
                  </Box>
                </>
              )}
              {reconcileDialog.result.matchedCount === 0 && reconcileDialog.result.onlyInWebhooksCount === 0 && reconcileDialog.result.onlyInAdoCount === 0 && (
                <Typography color="text.secondary">No work items on this date in webhooks or ADO.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReconcile}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Processing queue (processed vs not processed) – below date-wise summary */}
      <WebhookQueueStatus />
    </Box>
  );
};

export default WebhookLogViewer;
